import { type CURRENCY, type PAYMENT_METHOD, type UUID, silenceError } from '@nfctron/nexus-core';

import * as Sentry from '@sentry/nextjs';
import isEmptyObject from 'lodash/isEmpty';

import { currencyEnumTickets } from '@root/lib/constants/currency';

import { FbPixelAdapter } from './fb-pixel.adapter';
import { type GoogleTagManagerAdapter } from './google-tag-manager.adapter';
import { GtagJsAdapter } from './gtag-js.adapter';
import { type SklikAdapter } from './sklik.adapter';
import { TikTokPixelAdapter } from './tiktok-pixel.adapter';

/**
 * Counter limits for events
 * @private
 */
const COUNTER_LIMITS = {
	initiateCheckout: -1,
	purchase: -1,
	purchaseSuccess: 2,
	purchaseFail: 2,
	addToCart: -1,
	removeFromCart: -1,
} as const;

/**
 * Tracking module
 * @author filipditrich <ditrich@nfctron.com>
 * @export
 */
export class TrackingModule {
	public fbPixelAdapter: FbPixelAdapter;
	public gtagJsAdapter: GtagJsAdapter;
	public googleTagManagerAdapter: GoogleTagManagerAdapter;
	public tiktokPixelAdapter: TikTokPixelAdapter;
	public sklikAdapter: SklikAdapter;

	/** internal event counter */
	private counter: Record<keyof typeof COUNTER_LIMITS, Map<string, number>> = {
		initiateCheckout: new Map(),
		purchase: new Map(),
		purchaseSuccess: new Map(),
		purchaseFail: new Map(),
		addToCart: new Map(),
		removeFromCart: new Map(),
	};

	constructor(
		fbPixelAdapter: FbPixelAdapter,
		gtagJsAdapter: GtagJsAdapter,
		googleTagManagerAdapter: GoogleTagManagerAdapter,
		tiktokPixelAdapter: TikTokPixelAdapter,
		sklikAdapter: SklikAdapter,
	) {
		this.fbPixelAdapter = fbPixelAdapter;
		this.gtagJsAdapter = gtagJsAdapter;
		this.googleTagManagerAdapter = googleTagManagerAdapter;
		this.tiktokPixelAdapter = tiktokPixelAdapter;
		this.sklikAdapter = sklikAdapter;
	}

	/**
	 * Private wrapper to control event firing
	 * @param fn Function to be executed
	 * @param uniqueIds Unique identifiers for the event
	 * @param counterKey Counter key
	 */
	private _wrap = (fn: () => void, uniqueIds: Record<string, any>, counterKey: keyof typeof COUNTER_LIMITS) => {
		const isUnlimited = COUNTER_LIMITS[counterKey] === -1;
		const uniqueKey = JSON.stringify(uniqueIds);
		const foundViaUniqueKey = isEmptyObject(uniqueKey) ? 0 : this.counter[counterKey].get(uniqueKey);
		const currentFireCount = foundViaUniqueKey ?? 0;
		const isLimited = !isUnlimited && currentFireCount >= COUNTER_LIMITS[counterKey];
		if (!isLimited) {
			try {
				fn();
				this.counter[counterKey].set(uniqueKey, currentFireCount + 1);
				console.debug(
					`🕵 [TrackingModule] Fired event "${counterKey}", ${Math.max(-1, COUNTER_LIMITS[counterKey] - currentFireCount - 1)} events remaining.`,
				);
			} catch (err) {
				console.debug(`🕵 [TrackingModule] Error while firing event "${counterKey}": ${err}`);
				/** capture to sentry */
				Sentry.captureException(err, {
					tags: {
						module: 'TrackingModule',
					},
					extra: {
						uniqueIds,
						counterKey,
					},
				});
			}
		} else {
			console.debug(
				`🕵 [TrackingModule] Cannot fire event "${counterKey}", limit ${currentFireCount}/${COUNTER_LIMITS[counterKey]} reached.\n Unique key: ${uniqueKey}`,
			);
		}
	};

	/**
	 * Page view handler function
	 * @param url Page URL
	 */
	public handlePageViewChange = (url: string) => {
		this.googleTagManagerAdapter.handlePageView(url);
		this.gtagJsAdapter.handlePageView(url);
		this.fbPixelAdapter.handlePageView();
		this.tiktokPixelAdapter.handlePageView();
	};

	/**
	 * Safe tracking wrapper to prevent crashing while firing invalid events
	 * @param fn Tracking function
	 */
	public safeTracking = (fn: () => void) => silenceError(fn);

	/**
	 * InitiateCheckout event handler
	 * @param options InitiateCheckout options
	 * @param ref Tracking reference
	 */
	public initiateCheckout = (options: InitiateCheckoutOptions, ref: TrackingRef) =>
		this._wrap(
			() => {
				const currency = currencyEnumTickets.get(options.currencyId);
				/** Facebook Pixel adapter */
				this.fbPixelAdapter.initiateCheckout(
					{
						value: options.value / 100,
						currency: currency.isoChar,
						num_items: options.tickets.length,
						content_ids: FbPixelAdapter.convertTicketsToContentIds(options.tickets),
					},
					ref,
				);
				/** Gtag.js adapter */
				this.gtagJsAdapter.handleEvent('begin_checkout', {
					currency: currency.isoChar,
					value: options.value / 100,
					items: GtagJsAdapter.convertTicketsToItems(options.tickets),
				});
				/** Google Tag Manager adapter (only when Gtag is not active, otherwise it handles it itself) */
				if (this.gtagJsAdapter.gtagTrackingIds.length === 0) {
					this.googleTagManagerAdapter.beginCheckout(
						{
							ecommerce: {
								currency: currency.isoChar,
								value: options.value / 100,
								items: GtagJsAdapter.convertTicketsToItems(options.tickets),
							},
						},
						ref,
					);
				}
				/** TikTok Pixel adapter */
				this.tiktokPixelAdapter.initiateCheckout(
					{
						value: options.value / 100,
						currency: currency.isoChar as any,
						contents: TikTokPixelAdapter.convertTicketsToContents(options.tickets),
						quantity: options.tickets.length,
					},
					ref,
				);
				/** Sklik adapter */
				this.sklikAdapter.initiateCheckout(
					{
						value: options.value,
						currency: currency.isoChar,
						transactionId: ref.eventId,
					},
					ref,
				);
			},
			{ eventId: ref.eventId },
			'initiateCheckout',
		);

	/**
	 * Purchase event handler
	 * @param options Purchase options
	 * @param ref Tracking reference
	 */
	public purchase = (options: PurchaseOptions, ref: TrackingRefWithPurchase) =>
		this._wrap(
			() => {
				const currency = currencyEnumTickets.get(options.currencyId);
				/** Facebook Pixel adapter */
				this.fbPixelAdapter.purchase(
					{
						value: options.value / 100,
						currency: currency.isoChar,
						num_items: options.tickets.length,
						content_ids: FbPixelAdapter.convertTicketsToContentIds(options.tickets),
						content_type: 'product',
					},
					ref,
				);
				/** Gtag.js adapter */
				this.gtagJsAdapter.purchase(
					{
						transaction_id: options.orderId,
						currency: currency.isoChar,
						value: options.value / 100,
						items: GtagJsAdapter.convertTicketsToItems(options.tickets),
					},
					ref,
				);
				/** Google Tag Manager adapter (only when Gtag is not active, otherwise it handles it itself) */
				if (this.gtagJsAdapter.gtagTrackingIds.length === 0) {
					this.googleTagManagerAdapter.purchase(
						{
							transaction_id: options.orderId,
							ecommerce: {
								transaction_id: options.orderId,
								currency: currency.isoChar,
								value: options.value / 100,
								items: GtagJsAdapter.convertTicketsToItems(options.tickets),
							},
						},
						ref,
					);
				}
				/** TikTok Pixel adapter */
				this.tiktokPixelAdapter.placeAnOrder(
					{
						currency: currency.isoChar as any,
						value: options.value / 100,
						content_type: 'product_group',
						content_id: options.orderId,
						contents: TikTokPixelAdapter.convertTicketsToContents(options.tickets),
						quantity: options.tickets.length,
					},
					ref,
				);
				/** Sklik adapter */
				this.sklikAdapter.purchase(
					{
						value: options.value,
						currency: currency.isoChar,
						orderId: options.orderId,
						transactionId: ref.orderId,
					},
					ref,
				);
			},
			{
				eventId: ref.eventId,
				orderId: ref.orderId,
			},
			'purchase',
		);

	/**
	 * Purchase success event handler
	 * @param options PurchaseSuccess options
	 * @param ref Tracking reference
	 */
	public purchaseSuccess = (options: PurchaseSuccessOptions, ref: TrackingRefWithPurchase) =>
		this._wrap(
			() => {
				const currency = currencyEnumTickets.get(options.currencyId);
				/** Facebook Pixel adapter */
				this.fbPixelAdapter.purchaseSuccess(
					{
						order_id: options.orderId,
						order_number: options.orderNumber,
						amount: options.amount / 100,
						paid_mount: options.paidAmount / 100,
						currency: currency.isoChar,
						payment_method: options.paymentMethodId,
						created: options.created.toISOString(),
						paid: options.paid.toISOString(),
					},
					ref,
				);
				/** Gtag.js adapter */
				this.gtagJsAdapter.handleEvent(
					'purchase_success' as any,
					{
						value: options.amount / 100,
						currency: currencyEnumTickets.get(options.currencyId).isoChar,
						order_id: options.orderId,
					},
					ref,
				);
				/** Google Tag Manager adapter */
				this.googleTagManagerAdapter.pushEventToDataLayer(
					'purchase_success',
					{
						event: 'purchase_success',
						ecommerce: {
							currency: currency.isoChar,
							value: options.paidAmount / 100,
							transaction_id: options.orderId,
							payment_type: options.paymentMethodId,
						},
					},
					ref,
				);
				/** TikTok Pixel adapter */
				this.tiktokPixelAdapter.completePayment(
					{
						currency: currency.isoChar as any,
						value: options.paidAmount / 100,
						content_type: 'product_group',
						content_id: options.orderId,
						quantity: 1,
					},
					ref,
				);
				/** Sklik adapter */
				this.sklikAdapter.purchaseSuccess(
					{
						value: options.amount,
						currency: currency.isoChar,
						orderId: options.orderId,
						transactionId: ref.orderId,
					},
					ref,
				);
			},
			{
				eventId: ref.eventId,
				orderId: ref.orderId,
			},
			'purchaseSuccess',
		);

	/**
	 * Purchase fail event handler
	 * @param options PurchaseFail options
	 * @param ref Tracking reference
	 */
	public purchaseFail = (options: PurchaseFailOptions, ref: TrackingRefWithPurchase) =>
		this._wrap(
			() => {
				/** Facebook Pixel adapter */
				this.fbPixelAdapter.purchaseFail(
					{
						order_id: options.orderId,
						order_number: options.orderNumber,
						amount: options.amount / 100,
						currency: currencyEnumTickets.get(options.currencyId).isoChar,
						payment_method: options.paymentMethodId,
						created: options.created.toISOString(),
						canceled: options.canceled.toISOString(),
					},
					ref,
				);
				/** Gtag.js adapter */
				this.gtagJsAdapter.handleEvent(
					'purchase_cancel' as any,
					{
						value: options.amount / 100,
						currency: currencyEnumTickets.get(options.currencyId).isoChar,
						order_id: options.orderId,
					},
					ref,
				);
				/** Google Tag Manager adapter */
				this.googleTagManagerAdapter.pushEventToDataLayer(
					'purchase_cancel',
					{
						event: 'purchase_cancel',
						event_category: 'ecommerce',
						event_label: 'purchase_cancel',
						ecommerce: {
							currency: currencyEnumTickets.get(options.currencyId).isoChar,
							value: options.amount / 100,
							transaction_id: options.orderId,
							payment_type: options.paymentMethodId,
						},
					},
					ref,
				);
				/** TikTok Pixel adapter (not supported) */
				/** Sklik adapter */
				this.sklikAdapter.purchaseFail(
					{
						value: options.amount,
						currency: currencyEnumTickets.get(options.currencyId).isoChar,
						orderId: options.orderId,
						transactionId: ref.orderId,
					},
					ref,
				);
			},
			{
				eventId: ref.eventId,
				orderId: ref.orderId,
			},
			'purchaseFail',
		);

	/**
	 * AddToCart event handler
	 * @param options AddToCart options
	 * @param ref Tracking reference
	 */
	public addToCart = (options: AddToCartOptions, ref: TrackingRef) =>
		this._wrap(
			() => {
				const currency = currencyEnumTickets.get(options.currencyId);

				/** Facebook Pixel adapter */
				this.fbPixelAdapter.addToCart(
					{
						value: options.value / 100,
						currency: currency.isoChar,
						content_ids: FbPixelAdapter.convertTicketsToContentIds(options.tickets),
						content_type: 'product',
					},
					ref,
				);
				/** Gtag.js adapter */
				this.gtagJsAdapter.addToCart(
					{
						currency: currency.isoChar,
						value: options.value / 100,
						items: GtagJsAdapter.convertTicketsToItems(options.tickets),
					},
					ref,
				);
				/** Google Tag Manager adapter (only when Gtag is not active, otherwise it handles it itself) */
				if (this.gtagJsAdapter.gtagTrackingIds.length === 0) {
					this.googleTagManagerAdapter.addToCart(
						{
							ecommerce: {
								currency: currency.isoChar,
								value: options.value / 100,
								items: GtagJsAdapter.convertTicketsToItems(options.tickets),
							},
						},
						ref,
					);
				}
				/** TikTok Pixel adapter */
				this.tiktokPixelAdapter.addToCart(
					{
						currency: currency.isoChar as any,
						value: options.value / 100,
						contents: TikTokPixelAdapter.convertTicketsToContents(options.tickets),
						quantity: options.tickets.length,
					},
					ref,
				);
				/** Sklik adapter */
				this.sklikAdapter.addToCart(
					{
						value: options.value,
						currency: currency.isoChar,
						transactionId: ref.eventId,
					},
					ref,
				);
			},
			{},
			'addToCart',
		);

	/**
	 * RemoveFromCart event handler
	 * @param options RemoveFromCart options
	 * @param ref Tracking reference
	 */
	public removeFromCart = (options: RemoveFromCartOptions, ref: TrackingRef) =>
		this._wrap(
			() => {
				const currency = currencyEnumTickets.get(options.currencyId);
				/** Facebook Pixel adapter */
				this.fbPixelAdapter.removeFromCart(
					{
						currency: currency.isoChar,
						content_ids: FbPixelAdapter.convertTicketsToContentIds(options.tickets),
						content_type: 'product',
					},
					ref,
				);
				/** Gtag.js adapter */
				this.gtagJsAdapter.removeFromCart(
					{
						currency: currency.isoChar,
						value: options.value / 100,
						items: GtagJsAdapter.convertTicketsToItems(options.tickets),
					},
					ref,
				);
				/** Google Tag Manager adapter (only when Gtag is not active, otherwise it handles it itself) */
				if (this.gtagJsAdapter.gtagTrackingIds.length === 0) {
					this.googleTagManagerAdapter.removeFromCart(
						{
							ecommerce: {
								currency: currency.isoChar,
								value: options.value / 100,
								items: GtagJsAdapter.convertTicketsToItems(options.tickets),
							},
						},
						ref,
					);
				}
				/** Facebook Pixel adapter (not supported) */
				this.tiktokPixelAdapter.handleEvent(
					'ClickButton',
					{
						description: 'Remove from cart',
						content_type: 'product',
						value: options.value / 100,
						currency: currency.isoChar as any,
						contents: TikTokPixelAdapter.convertTicketsToContents(options.tickets),
					},
					ref,
				);
				/** Sklik adapter */
				this.sklikAdapter.removeFromCart(
					{
						value: options.value,
						currency: currency.isoChar,
						transactionId: ref.eventId,
					},
					ref,
				);
			},
			{},
			'removeFromCart',
		);
}

/**
 * Tracking Ticket options type
 * @export
 */
export type TrackingTicketOptions = {
	cartTicketId: UUID;
	ticketType: {
		ticketTypeId: UUID;
		namePub: string;
		displayOrder: number;
		price: number;
		ticketTypeGroup: {
			namePub: string;
		} | null;
	};
	chips: Array<{
		cartChipId: UUID;
		amount: number;
		productItemId: UUID;
	}>;
	extraProducts: Array<{
		ticketProductId: UUID;
		name: string;
		price: number;
		displayOrder: number;
		count: number;
		chips: Array<{
			cartChipId: UUID;
			amount: number;
			productItemId: UUID;
		}>;
	}>;
	holderName?: string;
	userData?: Record<string, any>;
	reservation: {
		seat: {
			reservationSeatId: UUID;
			seatId: UUID;
		} | null;
		crowd: {
			crowdId: UUID;
			reservationCrowdId: UUID;
		} | null;
	};
	trackingContext: {
		eventName: string;
		eventPlace: string;
		eventLocation: string | undefined;
		eventGroups: Array<{ namePub: string }>;
	} | null;
};

/**
 * InitiateCheckout options type
 * @export
 */
export type InitiateCheckoutOptions = {
	value: number;
	currencyId: CURRENCY;
	tickets: Array<TrackingTicketOptions>;
};

/**
 * Purchase options type
 * @export
 */
export type PurchaseOptions = {
	value: number;
	currencyId: CURRENCY;
	orderId: UUID;
	tickets: Array<TrackingTicketOptions>;
};

/**
 * PurchaseSuccess options type
 * @export
 */
export type PurchaseSuccessOptions = {
	orderId: UUID;
	orderNumber: number;
	amount: number;
	paidAmount: number;
	paymentMethodId: PAYMENT_METHOD;
	currencyId: CURRENCY;
	created: Date;
	paid: Date;
};

/**
 * PurchaseFail options type
 * @export
 */
export type PurchaseFailOptions = {
	orderId: UUID;
	orderNumber: number;
	amount: number;
	paymentMethodId: PAYMENT_METHOD;
	currencyId: CURRENCY;
	created: Date;
	canceled: Date;
};

/**
 * AddToCart options type
 * @export
 */
export type AddToCartOptions = {
	value: number;
	currencyId: CURRENCY;
	tickets: Array<TrackingTicketOptions>;
};

/**
 * RemoveFromCart options type
 * @export
 */
export type RemoveFromCartOptions = {
	currencyId: CURRENCY;
	value: number;
	tickets: Array<TrackingTicketOptions>;
};

/**
 * Common tracking reference type
 * @export
 */
export type TrackingRef = { eventId: UUID };
export type TrackingRefWithPurchase = TrackingRef & { orderId: UUID };
